import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { PINNED_PRESET_ROUTE_PATH, useTablePresets } from '@/entities/settings';
import { useAppNavigate } from '@/shared/lib';
import { useDialogsSection } from './useDialogsSection';

/**
 * Держит согласованными раздел, открытый адресом, и вид списка в нём.
 *
 * Раздел закреплённого пресета ведёт свой вид: заход показывает то, что пользователь в нём
 * оставил, — включая правки, не сохранённые в пресет. Из самого пресета вид берётся только на
 * первом заходе, пока в разделе не выбран ни один пресет: раздел «Диалоги» ничего у соседей не
 * перенимает и наоборот, поэтому переход между разделами вид ни одного из них не переписывает.
 * Адрес, которому не отвечает ни один закреплённый пресет (открепление, удаление, переименование,
 * ссылка в никуда), уводит в раздел «Диалоги» — но только когда набор пресетов известен целиком:
 * пока он не получен, адрес раздела несуществующим не считается. Открытая адресом запись при этом
 * переносится в «Диалоги» вместе с условиями её открытия: запись доступна независимо от пресета,
 * и адрес, скопированный из раздела пресета, не должен терять диалог у того, кому этот пресет
 * не закреплён.
 *
 * Закрепление и открепление применённого пресета его не снимают, а переводят между разделами:
 * пресет, закреплённый в «Диалогах», уходит из их вида в свой появившийся раздел, а пресет,
 * откреплённый в своём разделе, остаётся применённым в «Диалогах», куда уводит адрес.
 *
 * Раздел серверного пресета, который удалили, вернули в личные или открепили — в том числе в другом
 * сеансе или его владелец, — забывается вместе со своим видом, как и раздел личного пресета.
 *
 * Живёт на уровне страницы, а не списка: в раскладках, где список рядом с открытой записью не
 * показан, связь адреса и раздела должна работать так же.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`); `null` — раздел без пресетов.
 */
export const usePresetSectionSync = (tableName: string | null) => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id?: string }>();
  const { sectionKey, sectionRoute, isUnknownSection, pinnedRoutes } = useDialogsSection(tableName ?? '');
  const { presets, isPresetListKnown, activePresetId, apply, clearActive, pruneServerSections } =
    useTablePresets(sectionKey);

  // Закрепление пресетов и пресет открытого раздела на предыдущем проходе. Закрепление
  // и открепление, сделанные прямо сейчас, по одному текущему составу не отличить от уже
  // закреплённого пресета, применённого в «Диалогах» до загрузки, и от адреса пресета, которого
  // нет: решение принимается по тому, что изменилось.
  const previousPinnedStateRef = useRef<Map<string, boolean> | null>(null);
  const previousSectionPresetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (tableName && isPresetListKnown) pruneServerSections();
  }, [tableName, isPresetListKnown, pruneServerSections]);

  useEffect(() => {
    // Раздел без пресетов адрес с ними не связывает — своего раздела у пресета там нет.
    if (!tableName) return;
    // Пока набор пресетов не известен целиком, «пресета нет» отличить от «пресет ещё не пришёл»
    // нельзя: системные и совместные пресеты приходят отдельно и позже личных, и раздел такого
    // пресета терялся бы на каждой перезагрузке и при заходе по прямой ссылке.
    if (!isPresetListKnown) return;

    const previousPinnedState = previousPinnedStateRef.current;
    previousPinnedStateRef.current = new Map(presets.map((preset) => [preset.id, preset.pinned]));
    const previousSectionPresetId = previousSectionPresetIdRef.current;
    previousSectionPresetIdRef.current = sectionRoute?.preset.id ?? null;

    if (isUnknownSection) {
      // Пресет открытого раздела откреплён, но не удалён: своего раздела у него больше нет,
      // и применённым он остаётся в «Диалогах» — уйти с адреса без него значило бы его снять.
      const unpinnedPreset = presets.find((preset) => preset.id === previousSectionPresetId && !preset.pinned);
      if (unpinnedPreset && previousPinnedState?.get(unpinnedPreset.id) === true) apply(unpinnedPreset.id);
      // Условия открытия карточки живут в query адреса, поэтому с записью переносятся и они;
      // без записи в адресе переносить нечего — раздел показывает свой список.
      navigate('/dialogs/:id?', { id: id ?? null }, { replace: true, saveParams: Boolean(id) });
      return;
    }
    if (!sectionRoute) {
      // Пресет, применённый в «Диалогах», закрепили только что: раздел у него теперь свой,
      // и остаться применённым он может только там. «Диалоги» возвращаются к виду до него,
      // а раздел начинается с сохранённого вида пресета (первый заход ниже). Замена адреса,
      // а не переход, — возврат браузером ведёт туда, откуда пользователь пришёл.
      const pinnedActiveRoute = pinnedRoutes.find((route) => route.preset.id === activePresetId);
      if (!pinnedActiveRoute || previousPinnedState?.get(pinnedActiveRoute.preset.id) !== false) return;
      clearActive();
      navigate(
        PINNED_PRESET_ROUTE_PATH,
        { slug: pinnedActiveRoute.slug, id: id ?? null },
        { replace: true, saveParams: Boolean(id) },
      );
      return;
    }
    // Раздел без выбранного пресета — либо он открыт впервые, либо заведён прежними версиями
    // настроек: показываем вид самого пресета. Дальше вид раздела ведёт пользователь.
    if (activePresetId === undefined) {
      apply(sectionRoute.preset.id);
    }
  }, [
    tableName,
    id,
    presets,
    pinnedRoutes,
    sectionRoute,
    isUnknownSection,
    isPresetListKnown,
    activePresetId,
    apply,
    clearActive,
    navigate,
  ]);
};
